export enum Currency {
  EUR = 'EUR',
  USD = 'USD',
  GBP = 'GBP',
  CHF = 'CHF',
  JPY = 'JPY',
  CNY = 'CNY',
}

export enum CatalogueItemCategory {
  CUSTOM = 'custom',
  STANDARD = 'standard',
  DUMMY = 'dummy',
}

export enum PromotionRequestStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export interface CatalogueItemPromotionRequestSummary {
  uuid: string;
  status: PromotionRequestStatus;
  createdAt: string;
  reviewedAt?: string;
  comment?: string;
}

export interface CatalogueItemPromotionRequest {
  uuid: string;
  itemUuid: string;
  itemName: string;
  requesterUuid: string;
  requesterName: string;
  requesterEmail: string;
  status: PromotionRequestStatus;
  createdAt: string;
  reviewerUuid?: string;
  reviewerName?: string;
  reviewedAt?: string;
  comment?: string;
}

export interface PromotionRequestsParams {
  page?: number;
  limit?: number;
  status?: PromotionRequestStatus;
}

export interface CatalogueItemCustomField {
  type: 'string' | 'integer' | 'float';
  value: string | number;
}

export interface CatalogueItem {
  uuid: string;
  erpId?: string;
  name: string;
  description?: string;
  category: CatalogueItemCategory;
  groupUuid: string;
  cost: number;
  currency: Currency;
  hyperlink?: string;
  createdAt: string;
  creatorUuid?: string;
  previousVersionUuid?: string;
  version?: number;
  nextVersionUuid?: string;
  drawingNumber?: string;
  isAssembly?: boolean;

  // Technical specifications
  activationPrinciple?: string;
  gripControlSensor?: boolean;
  distanceSensor?: boolean;
  weightG?: number;
  strokeMm?: number;
  maxGripForceN?: number;
  ipClass?: string;
  materialType?: string;
  airSupplyNeeded?: boolean;
  payloadKg?: number;
  reachMm?: number;
  dof?: number;
  vacuumSupplyNeeded?: boolean;
  pneumaticTubeDiameterMm?: number;
  robotVersion?: string;

  // Custom fields
  customFields?: Record<string, CatalogueItemCustomField> | null;

  // Additional fields
  images?: string[];
  files?: string[];

  // Promotion request
  promotionRequest?: CatalogueItemPromotionRequestSummary;

  // Permission flag from API
  canEdit?: boolean;
}

export interface CatalogueItemGroup {
  uuid: string;
  name: string;
  parentUuid?: string;
  children: CatalogueItemGroup[];
  items: CatalogueItem[];
}

export interface ChildItemData {
  uuid: string;
  quantity: number;
}

export interface ChildItemWithQuantity extends CatalogueItem {
  quantity: number;
}

export interface CatalogueItemDetail extends CatalogueItem {
  children: ChildItemWithQuantity[];
  parents: CatalogueItem[];
  changelog?: ChangelogEntry[];
}

export interface BomItem {
  uuid: string;
  name: string;
  quantity: number;
  unitCost: number;
  cost: number;
  children: BomItem[];
}

export interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

export enum CatalogueBrowserMode {
  BROWSE = 'browse',
  SELECT = 'select',
}

// ERP Import types

export type ErpImportMode = 'update' | 'create' | 'upsert';

export type ErpImportTargetField =
  | 'erp_id'
  | 'name'
  | 'description'
  | 'cost'
  | 'stock'
  | 'location';

export type ErpStockStatus = 'in_stock' | 'to_order' | 'kanban';

export interface ErpImportColumnMapping {
  source: string;
  target: ErpImportTargetField;
}

export interface ErpImportDefaults {
  category?: CatalogueItemCategory;
  groupUuid?: string;
  currency?: Currency;
  description?: string;
  location?: string;
  stockStatus?: ErpStockStatus;
}

export interface ErpImportConfig {
  mode: ErpImportMode;
  columnMapping: ErpImportColumnMapping[];
  defaults: ErpImportDefaults;
}

export interface ErpImportMissingItem {
  rowNumber: number;
  erpId: string;
}

export interface ErpImportFieldChange {
  previous: string | number | null;
  new: string | number | null;
}

export interface ErpImportUpdatedItem {
  uuid: string;
  erpId: string;
  name: string;
  rowNumber: number;
  changes: Record<string, ErpImportFieldChange>;
}

export interface ErpImportCreatedItem {
  uuid: string;
  erpId: string;
  name: string;
  cost: number;
  rowNumber: number;
}

export interface ErpImportResultResponse {
  totalRowsProcessed: number;
  itemsUpdated: number;
  itemsCreated: number;
  itemsSkipped: number;
  missingItems: ErpImportMissingItem[];
  updatedItems: ErpImportUpdatedItem[];
  createdItems: ErpImportCreatedItem[];
  errors: string[];
}

// ERP Import Log types

export interface ErpImportLog {
  uuid: string;
  userUuid: string;
  userName: string;
  filename: string | null;
  mode: ErpImportMode;
  totalRows: number;
  itemsCreated: number;
  itemsUpdated: number;
  itemsSkipped: number;
  errorsCount: number;
  createdAt: string;
}

// Changelog types

export type ChangelogAction =
  | 'created'
  | 'updated'
  | 'duplicated'
  | 'revised'
  | 'childAdded'
  | 'childRemoved'
  | 'childQuantityUpdated'
  | 'imageAdded'
  | 'imageRemoved'
  | 'fileAdded'
  | 'fileRemoved'
  | 'erpImported'
  | 'promoted';

export interface ChangelogEntry {
  uuid: string;
  userName: string;
  action: ChangelogAction;
  timestamp: Date;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  changes: Record<string, any> | null;
}
